import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import {
	Button,
	CollapsibleSection,
	Dialog,
	Modal,
	ModuleHeader,
	type ModuleHeaderProps,
	ReviewersGroup,
	Text,
} from '@viapeople/components'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../admin/src/components/GenericTable'
import { trpc } from '../../utils/trpc'
import { useReviewerSelection } from './useReviewerSelection'

export type RouterOutput = inferRouterOutputs<AppRouter>

export type GroupReviewers = RouterOutput['client']['cycles']['reviewers'][
	| 'searchPossibleAsParticipant'
	| 'searchPossibleAsManager']

type HeaderItem = {
	label: string
	value: string
}

type CycleAction = {
	id: string
	key: string
	movesForward: boolean
}

const getReviewerActionLabel = (key: string) => {
	switch (key) {
		case 'approve':
			return 'Approve list'
		case 'reject':
			return 'Reject list'
		case 'submit':
			return 'Submit list'
		default:
			return key.charAt(0).toUpperCase() + key.slice(1)
	}
}

const getReviewerActionVariant = (key: string, movesForward: boolean) => {
	if (key === 'reject') return 'secondary' as const
	if (movesForward) return 'primary' as const
	return 'secondary' as const
}

const getReviewerActionButtons = (
	cycleActions: CycleAction[],
	hasChanges: boolean,
	handlers: {
		handleSaveReviewers: () => void
		cancelEditing: () => void
		handleApproveReviewers: () => void
		handleRejectReviewers: () => void
		handleSubmitReviewers: () => void
	},
) => {
	if (hasChanges) {
		return [
			{
				label: 'Save changes',
				onClick: handlers.handleSaveReviewers,
				variant: 'primary' as const,
			},
			{
				label: 'Cancel',
				onClick: handlers.cancelEditing,
				variant: 'secondary' as const,
			},
		]
	}

	// Otherwise, show buttons based on available cycle actions
	return cycleActions.map((action) => {
		const getHandler = () => {
			switch (action.key) {
				case 'approve':
					return handlers.handleApproveReviewers
				case 'reject':
					return handlers.handleRejectReviewers
				case 'submit':
					return handlers.handleSubmitReviewers
				default:
					return () => {}
			}
		}

		return {
			label: getReviewerActionLabel(action.key),
			onClick: getHandler(),
			variant: getReviewerActionVariant(action.key, action.movesForward),
		}
	})
}

export function ReviewerSelectionPage({ isManagerView = true }: { isManagerView?: boolean }) {
	const { cycleId, taskId } = useParams<{ cycleId: string; taskId: string }>()
	const navigate = useNavigate()
	const {
		taskData,
		hasChanges,
		validateReviewersByGroups,
		cancelEditing,
		handleSaveReviewers,
		handleSubmitReviewers,
		handleApproveReviewers,
		handleRejectReviewers,
		editedReviewers,
		setEditedReviewers,
	} = useReviewerSelection(isManagerView)

	const { data: cycleData } = useQuery({
		...trpc.client.cycles.show.queryOptions({
			id: cycleId || '',
		}),
		enabled: !!cycleId,
	})

	const isTaskCompleted = taskData?.taskStatus === 'completed'

	const moduleHeaderButtons = useMemo((): ModuleHeaderProps['buttons'] => {
		const cycleActions = (taskData?.cycleActions || []) as CycleAction[]

		return isTaskCompleted
			? [
					{
						label: 'Close',
						onClick: () => {
							navigate('/dashboard')
						},
						variant: 'secondary',
					},
				]
			: getReviewerActionButtons(cycleActions, hasChanges, {
					handleSaveReviewers,
					cancelEditing,
					handleApproveReviewers,
					handleRejectReviewers,
					handleSubmitReviewers,
				})
	}, [
		taskData?.cycleActions,
		hasChanges,
		navigate,
		isTaskCompleted,
		handleSaveReviewers,
		cancelEditing,
		handleApproveReviewers,
		handleRejectReviewers,
		handleSubmitReviewers,
	])

	const [isModalOpen, setIsModalOpen] = useState<string | null>(null)
	const [selectedReports, setSelectedReports] = useState<string[]>([])
	const [selectedReviewersData, setSelectedReviewersData] = useState<GroupReviewers['data']>([])

	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)

	const headerData: HeaderItem[] = [
		{
			label: 'Employee',
			value: taskData?.participant ? `${taskData.participant.firstName} ${taskData.participant.lastName}` : 'N/A',
		},
		{
			label: 'Primary manager',
			value: taskData?.directReportTo
				? `${taskData.directReportTo.firstName} ${taskData.directReportTo.lastName}`
				: 'N/A',
		},
		...(taskData?.secondaryReportTo
			? [
					{
						label: 'Secondary manager',
						value: `${taskData.secondaryReportTo.firstName} ${taskData.secondaryReportTo.lastName}`,
					},
				]
			: []),
		{
			label: 'Status',
			value: taskData?.cycleReviewersStatus || 'N/A',
		},
	]

	const openModal = (groupId: string) => {
		setIsModalOpen(groupId)
	}

	const closeModal = () => {
		setIsModalOpen(null)
		setSelectedReports([])
	}

	const columns: Column<NonNullable<GroupReviewers['data'][number]>>[] = [
		{
			field: 'firstName',
			label: 'First Name',
			colSpan: 1,
		},
		{
			field: 'lastName',
			label: 'Last Name',
			colSpan: 1,
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
		},
	]

	// Memoize the query options generator to ensure stable reference
	const queryOptionsGenerator = useCallback(
		(options: { search?: string }) => {
			if (!cycleId || !taskId || !taskData?.participant?.externalId) return

			return {
				...(isManagerView
					? trpc.client.cycles.reviewers.searchPossibleAsManager.queryOptions({
							...options,
							cycleId,
							taskId,
							groupId: isModalOpen || undefined,
						})
					: trpc.client.cycles.reviewers.searchPossibleAsParticipant.queryOptions({
							...options,
							cycleId,
							taskId,
							groupId: isModalOpen || undefined,
						})),
			}
		},
		[cycleId, taskId, taskData?.participant?.externalId, isManagerView, isModalOpen],
	)

	return (
		<main className='w-full p-6'>
			<div>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Dashboard', to: '/dashboard' },
						{ label: cycleData?.name || '' },
						{ label: isManagerView ? 'Approve Reviewers' : 'Edit Reviewers' },
					]}
					title={isManagerView ? 'Approve Reviewers' : 'Edit Reviewers'}
					buttons={moduleHeaderButtons}
				/>
			</div>

			<div className='mt-6'>
				{taskData?.instructions && (
					<CollapsibleSection title='Instructions' defaultOpen={false}>
						<Text variant='small' className='text-neutral-600 mt-2'>
							{taskData.instructions}
						</Text>
					</CollapsibleSection>
				)}
			</div>

			<div className='mt-6'>
				<div className='mt-4 border border-neutral-200 rounded-lg'>
					<div className='px-6 py-4 border-b border-neutral-200 flex items-center justify-between'>
						<div className='flex items-center gap-2'>
							<Text variant='medium' weight='semibold'>
								Reviewer list
							</Text>
						</div>
					</div>
					<div className='p-6'>
						<div className='flex justify-between items-start gap-4 mb-8'>
							<div className='grid grid-cols-4 gap-4 justify-items-start'>
								{headerData.map((item: HeaderItem) => (
									<div key={item.label}>
										<Text variant='xsmall' className='text-neutral-600 whitespace-nowrap'>
											{item.label}
										</Text>
										<Text variant='small' weight='semibold' className='whitespace-nowrap'>
											{item.value}
										</Text>
									</div>
								))}
							</div>
						</div>
						{taskData?.groups?.map((group) => {
							return (
								<ReviewersGroup
									key={group.id}
									group={{
										id: group.id,
										name: group.name,
										minReviewers: group.minReviewers,
										maxReviewers: group.maxReviewers,
										reviewers: (editedReviewers[group.id]?.reviewers || []).map((reviewer) => ({
											externalId: reviewer.externalId,
											firstName: reviewer.firstName,
											lastName: reviewer.lastName,
										})),
									}}
									allowEditing={
										(!isManagerView && !isTaskCompleted) ||
										(isManagerView && taskData?.allowReviewerEdit && !isTaskCompleted)
									}
									error={validateReviewersByGroups().errors[group.id]}
									onRemoveReviewer={(newReviewers, groupId) => {
										setEditedReviewers((prev) => ({
											...prev,
											[groupId]: {
												...prev[groupId],
												reviewers: newReviewers.map((r) => {
													const [firstName, ...rest] = r.label.split(' ')
													const lastName = rest.join(' ')
													return { externalId: r.id, firstName, lastName }
												}),
											},
										}))
									}}
									onAddClick={() => openModal(group.id)}
								/>
							)
						})}
					</div>
				</div>
			</div>

			{taskData?.groups?.map((group) => (
				<Modal
					key={group.id}
					isOpen={isModalOpen === group.id}
					onClose={closeModal}
					title={`Add to ${group.name}`}
					fullScreen>
					<div className='flex flex-col h-[calc(100vh-150px)]'>
						<GenericTable<GroupReviewers['data'][number], { search?: string }>
							queryOptionsGenerator={queryOptionsGenerator}
							isInternal
							selectable
							onSelectionChange={(selected: GroupReviewers['data']) => {
								setSelectedReports(selected.map((r) => r.externalId))
								setSelectedReviewersData(selected)
							}}
							columns={columns}
							showPagination={true}
						/>
					</div>
					<div className='p-4 flex justify-end'>
						<Button variant='secondary' onClick={closeModal} className='mr-2'>
							Cancel
						</Button>
						<Button
							variant='primary'
							onClick={() => {
								if (isModalOpen) {
									setEditedReviewers((prev) => ({
										...prev,
										[isModalOpen]: {
											...prev[isModalOpen],
											id: isModalOpen,
											name: taskData?.groups?.find((g) => g.id === isModalOpen)?.name || '',
											reviewers: [
												...(prev[isModalOpen]?.reviewers || []),
												...selectedReviewersData.map((r: NonNullable<GroupReviewers['data'][number]>) => ({
													externalId: r.externalId,
													firstName: r.firstName,
													lastName: r.lastName,
												})),
											],
										},
									}))
									closeModal()
								}
							}}
							disabled={selectedReports.length === 0}>
							Add Selected ({selectedReports.length})
						</Button>
					</div>
				</Modal>
			))}
			<Dialog
				isOpen={isRejectModalOpen}
				onClose={() => setIsRejectModalOpen(false)}
				title='Reject reviewer list'
				message='Click the button below to reject the reviewer list.'
				onConfirm={handleRejectReviewers}
				confirmLabel='Reject list'
				type='delete'
			/>
		</main>
	)
}
